import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as DotIcon } from 'src/assets/tailwind/Sessions/Dot.svg';
import {
  ERROR_SESSION_POLLING_INTERVAL_MS,
  UPLOAD_SESSION_POLLING_INTERVAL_MS
} from 'src/config';
import { ApiSessionStatus } from 'src/content/DataLibrary/UploadSession/enum';
import NotificationsPopover from 'src/content/Notifications';
import { useUploadWidget } from 'src/context/UploadWidgetContext';
import useCanUseWorkflows from 'src/hooks/useCanUseWorkflows';
import usePermission from 'src/hooks/usePermission';
import useSubscriptionPlan from 'src/hooks/useSubscriptionPlan';
import useUpdateAccessRights from 'src/hooks/useUpdateAccessRights';
import { useListUploadSessionsQuery } from 'src/newServices/uploadSessions';
import type { RootState } from 'src/redux/store';
import { useSelector } from 'src/redux/store';
import { FEATURE_PERMISSIONS, USER_APP_PERMISSIONS } from 'src/types/enum';
import { NAVIGATIONS } from 'src/utils/constants';
import { getInitials } from 'src/utils/utils';

import {
  CollapsedHistory,
  LicenseLimitReachedBadge,
  TrialBadge
} from './components';
import type { SidebarState } from './hooks';
import { useNewChat, useSidebarState } from './hooks';
import {
  AdminConsoleNav,
  BottomSlot,
  Footer,
  PrimaryNav,
  Profile,
  ScrollableList
} from './sections';
import { SidebarMode } from './types';
import { buildAdminConsoleItems, buildSidebarMenuItems } from './utils';

export interface SidebarV2Props {
  state?: SidebarState;
}

const SidebarV2 = ({ state }: SidebarV2Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const fallback = useSidebarState();
  const { isOpen, mode, toggle } = state ?? fallback;

  const user = useSelector((s: RootState) => s.auth.user);
  const isAuthorized = useSelector((s: RootState) => s.auth.isAuthorized);
  const isLangInitialized = useSelector(
    (s: RootState) => s.data.isLangInitialized
  );
  const { isSysAdminUser } = useUpdateAccessRights();
  const { isTrialPlan } = useSubscriptionPlan();
  const { startNewChat } = useNewChat();
  const { progress } = useUploadWidget();
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const [uploadSessionErrorAcknowledged, setUploadSessionErrorAcknowledged] = useState(false);

  const { data: activeSessionData } = useListUploadSessionsQuery(
    { params: { view: ApiSessionStatus.ACTIVE, ps: 1, p: 0 } },
    {
      refetchOnMountOrArgChange: true,
      pollingInterval: hasActiveSession
        ? UPLOAD_SESSION_POLLING_INTERVAL_MS
        : 0
    }
  );
  const { data: errorSessionData } = useListUploadSessionsQuery(
    {
      params: { status: ApiSessionStatus.COMPLETED_WITH_FAILURES, ps: 1, p: 0 }
    },
    { pollingInterval: ERROR_SESSION_POLLING_INTERVAL_MS }
  );

  useEffect(() => {
    const activeRows =
      activeSessionData?.data ?? activeSessionData?.ret_val?.data ?? [];
    setHasActiveSession(Array.isArray(activeRows) && activeRows.length > 0);
  }, [activeSessionData]);

  useEffect(() => {
    const errorRows =
      errorSessionData?.data ?? errorSessionData?.ret_val?.data ?? [];
    const hasError =
      (Array.isArray(errorRows) && errorRows.length > 0) ||
      progress.failedCount > 0;
    if (pathname.startsWith(NAVIGATIONS.UPLOAD_SESSIONS) && hasError) {
      setUploadSessionErrorAcknowledged(true);
    } else if (!hasError) {
      setUploadSessionErrorAcknowledged(false);
    }
  }, [pathname, errorSessionData, progress.failedCount]);

  const workspacesDot = useMemo(() => {
    const errorRows =
      errorSessionData?.data ?? errorSessionData?.ret_val?.data ?? [];
    const hasErrorSession =
      (Array.isArray(errorRows) && errorRows.length > 0) ||
      progress.failedCount > 0;
    const showError = hasErrorSession && !uploadSessionErrorAcknowledged;
    if (!hasActiveSession && !showError) return undefined;
    return {
      endIcon: DotIcon,
      endIconColorClass: showError ? 'tw:text-icon-error' : 'tw:text-icon-success'
    };
  }, [hasActiveSession, errorSessionData, progress.failedCount, uploadSessionErrorAcknowledged]);

  const canViewWorkspaces = usePermission(
    FEATURE_PERMISSIONS.WORKSPACES_ENTERPRISE_KNOWLEDGE_CAPABILITY,
    { fallbackAppPermKey: USER_APP_PERMISSIONS.VIEW_ROOT_FOLDER_CONTENT }
  );
  const canUseChat = usePermission(FEATURE_PERMISSIONS.CHAT);
  const canUseAssistants = usePermission(FEATURE_PERMISSIONS.ASSISTANTS);
  const canUseWorkflows = useCanUseWorkflows();
  const canUseProjects = usePermission(FEATURE_PERMISSIONS.PROJECTS);
  const canUseApps = usePermission(FEATURE_PERMISSIONS.APPS);
  const canUseAdvanceSearch = usePermission(
    FEATURE_PERMISSIONS.ADVANCE_SEARCH
  );
  const canUseConnectors = usePermission(FEATURE_PERMISSIONS.CONNECTORS);
  const primaryItems = buildSidebarMenuItems(
    t,
    startNewChat,
    canViewWorkspaces,
    workspacesDot,
    {
      canUseChat,
      canUseAssistants,
      canUseWorkflows,
      canUseProjects,
      canUseApps,
      canUseAdvanceSearch,
      canUseConnectors
    }
  );
  const adminItems = buildAdminConsoleItems(t, navigate, pathname);

  const name = user?.name ?? '';
  const initials = getInitials(name) || 'U';
  const collapsed = !isOpen;
  const effectiveMode = isSysAdminUser ? SidebarMode.ADMIN : mode;

  return (
    <div className="tw:flex tw:h-full tw:flex-col tw:justify-between tw:bg-main tw:p-m">
      <div
        className={
          collapsed
            ? 'tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:items-center tw:gap-m'
            : 'tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-s'
        }
      >
        <Profile initials={initials} collapsed={collapsed} onToggle={toggle} />

        {effectiveMode === SidebarMode.ADMIN && (
          <AdminConsoleNav
            items={adminItems}
            collapsed={collapsed}
            onExpand={toggle}
          />
        )}

        {effectiveMode === SidebarMode.DEFAULT && (
          <>
            <PrimaryNav items={primaryItems} collapsed={collapsed} />
            {collapsed ? (
              <CollapsedHistory onExpand={toggle} />
            ) : (
              <ScrollableList />
            )}
          </>
        )}
      </div>

      <div className="tw:flex tw:flex-col tw:gap-s">
        {!collapsed && (
          <BottomSlot>
            {isTrialPlan ? <TrialBadge /> : null}
            {isAuthorized && user && isLangInitialized ? (
              <LicenseLimitReachedBadge />
            ) : null}
          </BottomSlot>
        )}
        <Footer collapsed={collapsed} notification={<NotificationsPopover />} />
      </div>
    </div>
  );
};

export default SidebarV2;
